import { useState, useEffect, Fragment } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  Button,
  Alert,
  Tooltip,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
  Legend,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';
import { useMe } from '../api/hooks.js';
import { useActiveProject } from '../lib/activeProject.js';
import { api } from '../api/client.js';
import { SessionDrilldownDialog, type DrilldownParams } from '../components/SessionDrilldownDialog.js';
import { STATUS_COLOR, PRIORITY_COLOR, cap } from './ticketShared.js';
import { useT } from '../i18n/useT.js';

interface SessionBehavior {
  totalSessions: number;
  avgFrustration: number;
  rageClickRate: number;
  deadClickRate: number;
  sessionToTicketRate: number;
  deltas: { avgFrustration: number; rageClickRate: number; deadClickRate: number; sessionToTicketRate: number };
  series: { day: string; rageRate: number; deadRate: number }[];
  resolution: { withReplayHours: number | null; withoutReplayHours: number | null };
  versions: string[];
  devices: string[];
}

interface SegmentRow {
  segment: string;
  sessions: number;
  avgFrustration: number;
  rageRate: number;
  deadRate: number;
  jsErrorRate: number;
  bounceRate: number;
  avgDurationSec: number;
}
interface ProblematicPage {
  path: string;
  sessions: number;
  errors: number;
  frustration: number;
  score: number;
}
interface Analysis {
  segments: SegmentRow[];
  heatmap: { dow: number; hour: number; errors: number }[];
  topErrors: { message: string; kind: string; count: number; url: string | null }[];
  problematicPages: ProblematicPage[];
}

interface Support {
  total: number;
  createdInPeriod: number;
  resolved: number;
  open: number;
  linkedToReplay: number;
  avgFrustration: number;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  series: { day: string; created: number; resolved: number }[];
  assignees: { id: string | null; name?: string; email?: string; open: number; resolved: number; medianHours: number | null }[];
  topTags: { tag: string; count: number }[];
}

const RANGES = [{ label: '7d', d: 7 }, { label: '30d', d: 30 }, { label: '90d', d: 90 }, { label: 'All', d: 0 }];
const EMERALD = '#2ec2b3';

export function SupportAnalyticsPage() {
  const t = useT().insights.support;
  const { data: me } = useMe();
  const orgs = me?.organizations ?? [];
  const [orgSel, setOrgSel] = useState('');
  const orgId = orgSel || orgs[0]?.id;
  const [days, setDays] = useState(30);
  const [version, setVersion] = useState('');
  const [device, setDevice] = useState('');
  const { selected: projectId, setProjectId, projects: projectList } = useActiveProject(orgId);

  // A release version and a device belong to one project's data. Carrying
  // them across a project switch filters the new project by something that
  // never happened in it, and the screen reads as empty rather than filtered.
  useEffect(() => {
    setVersion('');
    setDevice('');
  }, [projectId]);

  // Drill-down popup: which sessions sit behind a clicked chart value.
  const [drill, setDrill] = useState<{ title: string; subtitle?: string; params: DrilldownParams } | null>(null);
  const baseParams = (): DrilldownParams => ({
    days,
    version: version || undefined,
    device: device || undefined,
    projectId: projectId || undefined,
  });
  const openDrill = (title: string, extra: Partial<DrilldownParams>, subtitle?: string) =>
    setDrill({ title, subtitle, params: { ...baseParams(), ...extra } });

  const q = useQuery({
    enabled: !!orgId,
    queryKey: ['support-analytics', orgId, days],
    queryFn: async () => (await api.get(`/v1/orgs/${orgId}/insights/support?days=${days}`)).data as Support,
  });
  const d = q.data;

  const sb = useQuery({
    enabled: !!orgId,
    queryKey: ['session-behavior', orgId, days, version, device, projectId],
    queryFn: async () => {
      const p = new URLSearchParams({ days: String(days) });
      if (version) p.set('version', version);
      if (device) p.set('device', device);
      if (projectId) p.set('projectId', projectId);
      return (await api.get(`/v1/orgs/${orgId}/insights/session-behavior?${p}`)).data as SessionBehavior;
    },
  });
  const s = sb.data;
  const resolution = [
    { label: 'With replay', hours: s?.resolution.withReplayHours ?? 0 },
    { label: 'Without replay', hours: s?.resolution.withoutReplayHours ?? 0 },
  ];

  const filterQs = () => {
    const p = new URLSearchParams({ days: String(days) });
    if (version) p.set('version', version);
    if (device) p.set('device', device);
    if (projectId) p.set('projectId', projectId);
    return p.toString();
  };
  const analysis = useQuery({
    enabled: !!orgId,
    queryKey: ['analysis', orgId, days, version, device, projectId],
    queryFn: async () => (await api.get(`/v1/orgs/${orgId}/insights/analysis?${filterQs()}`)).data as Analysis,
  });
  const a = analysis.data;
  const rootCause = useMutation({
    mutationFn: async () =>
      (await api.post(`/v1/orgs/${orgId}/insights/root-cause?${filterQs()}`)).data as { text: string; source: 'ai' | 'heuristic' },
  });

  return (
    <Box>
      <Header
        icon={<SupportAgentIcon color="primary" />}
        title={t.title}
        subtitle={t.subtitle}
        days={days}
        setDays={setDays}
      />
      {(q.isLoading || sb.isLoading) && <LinearProgress sx={{ mb: 2 }} />}

      {/* Filter bar */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <FilterListIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <FilterSelect label={t.filters.organization} value={orgSel || orgs[0]?.id || ''} onChange={setOrgSel} options={orgs.map((o) => ({ value: o.id, label: o.name }))} allowAll={false} />
        <FilterSelect label={t.filters.module} value={projectId} onChange={setProjectId} options={projectList.map((p) => ({ value: p.id, label: p.name }))} allLabel={t.filters.allModules} />
        <FilterSelect label={t.filters.version} value={version} onChange={setVersion} options={(s?.versions ?? []).map((v) => ({ value: v, label: v }))} allLabel={t.filters.allVersions} />
        <FilterSelect label={t.filters.segment} value={device} onChange={setDevice} options={(s?.devices ?? []).map((v) => ({ value: v, label: cap(v) }))} allLabel={t.filters.allSegments} />
        {(projectId || version || device) && (
          <Chip size="small" variant="outlined" label="Clear" onClick={() => { setProjectId(''); setVersion(''); setDevice(''); }} onDelete={() => { setProjectId(''); setVersion(''); setDevice(''); }} />
        )}
      </Paper>

      {/* Session-intelligence KPIs */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
        <MetricCard icon={<LocalFireDepartmentIcon />} label={t.kpi.frustration} value={s ? s.avgFrustration.toFixed(1) : '—'} delta={s?.deltas.avgFrustration} onClick={() => openDrill(t.drill.frustrated, { minFrustration: 40 }, t.drill.frustratedHint)} />
        <MetricCard icon={<AdsClickIcon />} label={t.kpi.rage} value={s ? `${s.rageClickRate}%` : '—'} delta={s?.deltas.rageClickRate} onClick={() => openDrill(t.drill.rage, { hasRageClick: true })} />
        <MetricCard icon={<TouchAppIcon />} label={t.kpi.dead} value={s ? `${s.deadClickRate}%` : '—'} delta={s?.deltas.deadClickRate} onClick={() => openDrill(t.drill.dead, { hasDeadClick: true })} />
        <MetricCard icon={<ConfirmationNumberIcon />} label={t.kpi.ticketRate} value={s ? `${s.sessionToTicketRate}%` : '—'} delta={s?.deltas.sessionToTicketRate} />
      </Box>

      {/* Anomaly trends + resolution time */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 2 }}>
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>{t.anomalyTitle}</Typography>
          <Typography variant="caption" color="text.secondary">{t.anomalySubtitle}</Typography>
          <Box sx={{ height: 240, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={s?.series ?? []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis dataKey="day" fontSize={11} tickFormatter={(v: string) => v.slice(5)} />
                <YAxis fontSize={11} unit="%" />
                <RTooltip />
                <Legend />
                <Line type="monotone" dataKey="rageRate" name={t.rageSeries} stroke="#e11d48" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="deadRate" name={t.deadSeries} stroke="#f97316" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>{t.resolutionTitle}</Typography>
          <Typography variant="caption" color="text.secondary">{t.resolutionSubtitle}</Typography>
          <Box sx={{ height: 240, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={resolution}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis fontSize={11} unit="h" />
                <RTooltip formatter={(v: number) => [`${v}h`, t.avgResolution]} />
                <Bar dataKey="hours" radius={[6, 6, 0, 0]}>
                  <Cell fill={EMERALD} />
                  <Cell fill="#94a3b8" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>

      {/* AI Root Cause Analysis */}
      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesomeIcon sx={{ color: EMERALD }} />
            <Typography variant="subtitle1" fontWeight={700}>AI Root Cause Analysis</Typography>
            {rootCause.data && (
              <Chip size="small" label={rootCause.data.source === 'ai' ? t.ai : t.heuristic} color={rootCause.data.source === 'ai' ? 'primary' : 'default'} />
            )}
          </Stack>
          <Button size="small" variant="contained" startIcon={<AutoAwesomeIcon />} disabled={rootCause.isPending} onClick={() => rootCause.mutate()}>
            {rootCause.isPending ? t.analyzing : rootCause.data ? t.reanalyze : t.analyze}
          </Button>
        </Stack>
        {rootCause.data ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{rootCause.data.text}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Run an AI analysis of this window's signals (top errors, most-frustrated segment and click anomalies) to surface the likely root cause. Uses the model configured in AI &amp; Models (falls back to a rule-based summary).
          </Typography>
        )}
        {rootCause.isError && <Alert severity="error" sx={{ mt: 1 }}>{t.analysisFailed}</Alert>}
        {a && a.topErrors.length > 0 && (
          <Stack direction="row" spacing={0.75} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.75 }}>
            {a.topErrors.slice(0, 4).map((e, i) => (
              <Chip key={i} size="small" variant="outlined" label={`${e.message.slice(0, 40)} · ${e.count}×`} />
            ))}
          </Stack>
        )}
      </Paper>

      {/* Error Heatmap + Segment Comparison */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 2, mb: 2 }}>
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>Error Heatmap</Typography>
          <Typography variant="caption" color="text.secondary">JS errors by weekday &amp; hour</Typography>
          <ErrorHeatmap
            data={a?.heatmap ?? []}
            onCellClick={(dow, hour, n) =>
              openDrill(`${DOW[dow]} ${hour}:00 errors`, { dow, hour, hasError: true }, `${n} error(s) in this hour`)
            }
          />
        </Paper>
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>Segment Comparison</Typography>
          <Typography variant="caption" color="text.secondary">
            {(a?.segments ?? []).map((s) => cap(s.segment)).slice(0, 2).join(' vs ') || 'Segment'} behavior
          </Typography>
          <Box sx={{ height: 300, mt: 1 }}>
            {(a?.segments?.length ?? 0) === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={{ mt: 4, textAlign: 'center' }}>No segment data yet.</Typography>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData(a!.segments)} outerRadius="72%">
                  <PolarGrid />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                  {a!.segments.slice(0, 4).map((s, i) => (
                    <Radar
                      key={s.segment}
                      name={cap(s.segment)}
                      dataKey={s.segment}
                      stroke={SEGMENT_COLORS[i % SEGMENT_COLORS.length]}
                      fill={SEGMENT_COLORS[i % SEGMENT_COLORS.length]}
                      fillOpacity={0.12}
                    />
                  ))}
                  <Legend
                    wrapperStyle={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      const dk = (e as unknown as { dataKey?: unknown }).dataKey;
                      const seg = typeof dk === 'string' ? dk : undefined;
                      if (seg) openDrill(`Segment: ${cap(seg)}`, { device: seg }, 'Sessions in this segment');
                    }}
                  />
                  <RTooltip formatter={(v: number) => `${Math.round(v)} / 100`} />
                </RadarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Most Problematic Pages */}
      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>{t.problemPages}</Typography>
        <Typography variant="caption" color="text.secondary">{t.problemPagesSubtitle}</Typography>
        <ProblematicPages
          rows={a?.problematicPages ?? []}
          onRowClick={(p) => openDrill(p.path, { path: p.path }, t.drill.lastPage)}
        />
      </Paper>

      {/* Support operations (ticket volume / resolution / workload) */}
      <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ mb: 1, mt: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Support operations
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        <Kpi label={t.tickets.total} value={d?.total ?? 0} />
        <Kpi label={t.tickets.open} value={d?.open ?? 0} accent="#f59e0b" />
        <Kpi label={t.tickets.resolved} value={d?.resolved ?? 0} accent={EMERALD} />
        <Kpi label={t.tickets.fromReplay} value={d?.linkedToReplay ?? 0} />
      </Box>

      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{t.tickets.createdVsResolved}</Typography>
        <Box sx={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={d?.series ?? []}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis dataKey="day" fontSize={11} />
              <YAxis allowDecimals={false} fontSize={11} />
              <RTooltip />
              <Legend />
              <Area type="monotone" dataKey="created" stroke="#3b82f6" fill="#3b82f680" name={t.tickets.created} />
              <Area type="monotone" dataKey="resolved" stroke={EMERALD} fill={`${EMERALD}80`} name={t.tickets.resolvedSeries} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
        <BreakdownCard title={t.tickets.byStatus} data={d?.byStatus} colorFor={(k) => STATUS_COLOR[k as keyof typeof STATUS_COLOR] ?? '#64748b'} />
        <BreakdownCard title={t.tickets.byPriority} data={d?.byPriority} colorFor={(k) => PRIORITY_COLOR[k as keyof typeof PRIORITY_COLOR] ?? '#64748b'} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2 }}>
        <Paper sx={{ overflow: 'hidden' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ p: 2, pb: 1 }}>{t.tickets.workload}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t.tickets.assignee}</TableCell>
                <TableCell align="right">{t.tickets.open}</TableCell>
                <TableCell align="right">{t.tickets.resolved}</TableCell>
                <TableCell align="right">{t.tickets.medianResolution}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(d?.assignees ?? []).map((a, i) => (
                <TableRow key={a.id ?? `un-${i}`}>
                  <TableCell>{a.name || a.email || t.tickets.unassigned}</TableCell>
                  <TableCell align="right">{a.open}</TableCell>
                  <TableCell align="right">{a.resolved}</TableCell>
                  <TableCell align="right">{a.medianHours != null ? `${a.medianHours}h` : '—'}</TableCell>
                </TableRow>
              ))}
              {d && d.assignees.length === 0 && (
                <TableRow><TableCell colSpan={4}><Typography color="text.disabled" sx={{ p: 1 }}>{t.tickets.none}</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Top tags</Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
            {(d?.topTags ?? []).map((t) => (
              <Chip key={t.tag} size="small" label={`${t.tag} · ${t.count}`} />
            ))}
            {d && d.topTags.length === 0 && <Typography color="text.disabled" variant="body2">No tags used yet.</Typography>}
          </Stack>
        </Paper>
      </Box>

      <SessionDrilldownDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        orgId={orgId}
        title={drill?.title ?? ''}
        subtitle={drill?.subtitle}
        params={drill?.params ?? { days }}
      />
    </Box>
  );
}

function BreakdownCard({ title, data, colorFor }: { title: string; data?: Record<string, number>; colorFor: (k: string) => string }) {
  const t = useT().insights.support;
  const entries = Object.entries(data ?? {});
  const total = entries.reduce((n, [, v]) => n + v, 0) || 1;
  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>{title}</Typography>
      <Stack spacing={1}>
        {entries.length === 0 && <Typography color="text.disabled" variant="body2">{t.noData}</Typography>}
        {entries.map(([k, v]) => (
          <Box key={k}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
              <Typography variant="body2">{cap(k)}</Typography>
              <Typography variant="body2" fontWeight={700}>{v}</Typography>
            </Stack>
            <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden' }}>
              <Box sx={{ width: `${(v / total) * 100}%`, height: '100%', bgcolor: colorFor(k) }} />
            </Box>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

export function Header({ icon, title, subtitle, days, setDays }: { icon: React.ReactNode; title: string; subtitle: string; days: number; setDays: (d: number) => void }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {icon}
          <Typography variant="h5">{title}</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
      </Box>
      <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v != null && setDays(v)}>
        {RANGES.map((r) => (<ToggleButton key={r.label} value={r.d}>{r.label}</ToggleButton>))}
      </ToggleButtonGroup>
    </Stack>
  );
}

export function Kpi({ label, value, accent }: { label: string; value: string | number; accent?: string }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" fontWeight={700} sx={{ color: accent }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

const SEGMENT_COLORS = ['#2ec2b3', '#f59e0b', '#8b5cf6', '#3b82f6'];

// The six radar axes and the segment field each reads from.
const RADAR_METRICS: { key: keyof SegmentRow; label: string }[] = [
  { key: 'avgFrustration', label: 'Frustration' },
  { key: 'rageRate', label: 'Rage Clicks' },
  { key: 'jsErrorRate', label: 'JS Errors' },
  { key: 'deadRate', label: 'Dead Clicks' },
  { key: 'bounceRate', label: 'Bounce Rate' },
  { key: 'avgDurationSec', label: 'Avg Duration' },
];

/**
 * Reshapes per-segment metrics into radar rows (one per axis), normalising each
 * metric to 0–100 against the max across segments so the different units
 * (score, %, seconds) are comparable on one spider.
 */
function radarData(segments: SegmentRow[]): Record<string, number | string>[] {
  return RADAR_METRICS.map((m) => {
    const max = Math.max(...segments.map((s) => Number(s[m.key]) || 0), 0);
    const row: Record<string, number | string> = { metric: m.label };
    for (const s of segments) row[s.segment] = max > 0 ? Math.round(((Number(s[m.key]) || 0) / max) * 100) : 0;
    return row;
  });
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** A weekday × hour error-density grid; cell opacity scales with error count. */
function ErrorHeatmap({ data, onCellClick }: { data: { dow: number; hour: number; errors: number }[]; onCellClick?: (dow: number, hour: number, n: number) => void }) {
  const t = useT().insights.support;
  const grid = new Map<string, number>();
  let max = 0;
  for (const c of data) {
    grid.set(`${c.dow}-${c.hour}`, c.errors);
    if (c.errors > max) max = c.errors;
  }
  if (data.length === 0) {
    return <Typography variant="body2" color="text.disabled" sx={{ mt: 2 }}>{t.noErrors}</Typography>;
  }
  return (
    <Box sx={{ mt: 1.5, overflowX: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: `34px repeat(24, 1fr)`, gap: '2px', minWidth: 520 }}>
        <Box />
        {Array.from({ length: 24 }, (_, h) => (
          <Typography key={h} variant="caption" sx={{ fontSize: 8, textAlign: 'center', color: 'text.disabled' }}>
            {h % 3 === 0 ? h : ''}
          </Typography>
        ))}
        {DOW.map((label, dow) => (
          <Fragment key={dow}>
            <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary', pr: 0.5 }}>{label}</Typography>
            {Array.from({ length: 24 }, (_, h) => {
              const n = grid.get(`${dow}-${h}`) ?? 0;
              const intensity = max > 0 ? n / max : 0;
              return (
                <Tooltip key={`${dow}-${h}`} title={n ? t.heatCell(label, h, n) : ''} arrow disableInteractive>
                  <Box
                    onClick={n ? () => onCellClick?.(dow, h, n) : undefined}
                    sx={{
                      pt: '100%',
                      position: 'relative',
                      borderRadius: '2px',
                      bgcolor: n === 0 ? 'action.hover' : `rgba(225,29,72,${0.15 + intensity * 0.85})`,
                      cursor: n ? 'pointer' : 'default',
                    }}
                  />
                </Tooltip>
              );
            })}
          </Fragment>
        ))}
      </Box>
    </Box>
  );
}

/** Ranked list of pages by problem volume, with a severity bar per page. */
function ProblematicPages({ rows, onRowClick }: { rows: ProblematicPage[]; onRowClick?: (p: ProblematicPage) => void }) {
  const t = useT().insights.support;
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ mt: 3, textAlign: 'center' }}>
        No error or frustration signals on any page in this window.
      </Typography>
    );
  }
  const scoreColor = (s: number) => (s >= 75 ? '#dc2626' : s >= 50 ? '#f59e0b' : s >= 30 ? '#ca8a04' : '#16a34a');
  return (
    <Stack spacing={1.25} sx={{ mt: 1.5 }}>
      {rows.map((p) => (
        <Box
          key={p.path}
          onClick={() => onRowClick?.(p)}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5, cursor: onRowClick ? 'pointer' : 'default', '&:hover': onRowClick ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : undefined }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1 }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 14, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.path}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary">{p.sessions.toLocaleString()} sessions</Typography>
              <Typography sx={{ fontWeight: 700, color: scoreColor(p.score) }}>{p.score}%</Typography>
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.75 }}>
            <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden' }}>
              <Box sx={{ width: `${Math.min(100, p.score)}%`, height: '100%', bgcolor: scoreColor(p.score) }} />
            </Box>
            <Tooltip title={t.jsErrors}>
              <Typography variant="caption" sx={{ color: '#dc2626', flexShrink: 0 }}>⚠ {p.errors.toLocaleString()}</Typography>
            </Tooltip>
            <Tooltip title={t.rageClicks}>
              <Typography variant="caption" sx={{ color: '#f59e0b', flexShrink: 0 }}>🔥 {p.frustration.toLocaleString()}</Typography>
            </Tooltip>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

/** A compact labelled dropdown for the filter bar. */
function FilterSelect({
  label,
  value,
  onChange,
  options,
  allLabel = 'All',
  allowAll = true,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  allLabel?: string;
  allowAll?: boolean;
}) {
  return (
    <TextField select size="small" label={label} value={value} onChange={(e) => onChange(e.target.value)} sx={{ minWidth: 150 }}>
      {allowAll && <MenuItem value="">{allLabel}</MenuItem>}
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
      ))}
    </TextField>
  );
}

/**
 * A session-intelligence KPI: brand-tinted icon, big value, and a period-over-
 * period delta. These metrics are all "lower is better", so a rise is red and a
 * drop is green.
 */
function MetricCard({
  icon,
  label,
  value,
  delta,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  delta?: number;
  onClick?: () => void;
}) {
  const up = (delta ?? 0) > 0;
  const flat = !delta;
  const color = flat ? 'text.secondary' : up ? '#e11d48' : '#16a34a';
  return (
    <Paper
      onClick={onClick}
      sx={{ p: 2.5, cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { boxShadow: 3 } : undefined }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: `${EMERALD}1a`, color: EMERALD, display: 'grid', placeItems: 'center' }}>
          {icon}
        </Box>
        {delta != null && (
          <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color }}>
            {up ? <NorthEastIcon sx={{ fontSize: 15 }} /> : <SouthEastIcon sx={{ fontSize: 15 }} />}
            <Typography variant="caption" fontWeight={700}>{up ? '+' : ''}{delta}%</Typography>
          </Stack>
        )}
      </Stack>
      <Typography variant="h4" fontWeight={800} sx={{ mt: 1.5 }}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Paper>
  );
}
